/**
 * Render user-uploaded vector layers on the map.
 * Maps each Layer (server-side concept) to one or more MapLibre layers
 * named `ul-{layer.id}-{role}`, where role ∈ {'fill','line','circle'}.
 */
import maplibregl from 'maplibre-gl';
import { getFeatures } from '../api/layers.js';
import { APP } from '../state.js';
import { getStyle } from '../ui/styleStore.js';
import {
  sampleRamp, stepExpression, rampedSizes, matchExpression,
  BIVARIATE_PALETTES, bivariateExpression,
} from '../ui/colorRamps.js';
import { pushChoroplethLegend, pushCategoricalLegend, pushBivariateLegend } from '../ui/styleModal.js';
import { showMapLoading, hideMapLoading } from '../ui/mapLoading.js';

// Editorial atlas palette — sand, slate, moss, dusty rose, etc.
// Aligned with the `--c1`..`--c8` CSS variables in base.css.
const PALETTE = ['#04648d', '#98c262', '#4ea3ce', '#e0a93a', '#d65a51', '#7a9bbb', '#b8d290', '#5b7d8c'];
const MARTIN_URL  = import.meta.env.VITE_MARTIN_URL;
const TITILER_URL = import.meta.env.VITE_TITILER_URL;
const S3_BUCKET   = import.meta.env.VITE_S3_BUCKET || 'geohub';
// Threshold below which we just fetch GeoJSON (faster startup; tiny payload).
// Above it, we use vector tiles via Martin if it's configured.
const VECTOR_TILE_THRESHOLD = 500;
// Point layers above this feature count get clustered (counts at low zoom,
// individual points at high zoom). Below it, plain points render fine.
const POINT_CLUSTER_THRESHOLD = 100;
// Hard cap on a single GeoJSON fetch, matching the backend.
const MAX_GEOJSON_FEATURES = 100_000;

export class UserLayerManager {
  constructor(map) {
    this.map = map;
    this.loaded = new Map();   // layerId → { sourceId, layerIds[], geometryType, name }
  }

  paletteFor(id) { return PALETTE[id % PALETTE.length]; }

  isLoaded(id) { return this.loaded.has(id); }

  /**
   * Visible (loaded) vector layers whose features we have client-side.
   * Used by isochrone / buffer analyses to count features within a polygon.
   * Layers served via vector tiles aren't included — their features only
   * exist tile-by-tile, so we can't tally globally.
   */
  getVisibleVectorLayersWithFeatures() {
    const out = [];
    for (const [id, entry] of this.loaded.entries()) {
      if (Array.isArray(entry.features) && entry.features.length) {
        out.push({
          id, name: entry.name, geometryType: entry.geometryType,
          features: entry.features,
        });
      }
    }
    return out;
  }

  async show(layer) {
    if (this.loaded.has(layer.id)) {
      this._setVisibility(layer.id, true);
      return;
    }

    if (layer.layer_type === 'raster') {
      return this._showRaster(layer);
    }

    // Loading indicator for big layers — wrapping the whole vector path so
    // it covers the network fetch + MapLibre's source compile.
    const big = (layer.feature_count ?? 0) > 1000;
    if (big) showMapLoading(`Loading ${layer.name} · ${layer.feature_count.toLocaleString()} features…`);

    try {
      return await this._showVectorInner(layer);
    } finally {
      if (big) hideMapLoading();
    }
  }

  async _showVectorInner(layer) {
    const sourceId = `ul-src-${layer.id}`;
    const layerIds = [];
    const color = this.paletteFor(layer.id);
    const geomTypes = (layer.geometry_type || '').split(',').map(s => s.trim());
    const isPoint   = geomTypes.some(t => /Point/.test(t));
    const isLine    = geomTypes.some(t => /LineString/.test(t));
    const isPolygon = geomTypes.some(t => /Polygon/.test(t));

    // ---- Choose a source strategy ---------------------------------------
    //  • Points  → always GeoJSON so MapLibre clustering works. Fetch up to
    //              the layer's feature count (capped at 100k).
    //  • Lines / polygons with > 500 features → vector tiles via Martin.
    //  • Everything else → small GeoJSON.
    let sourceLayerName = null;
    let useCluster = false;
    let cachedFeatures = null;   // for analyses that read user-uploaded geometry (isochrone, buffer)

    if (isPoint) {
      const fetchLimit = Math.min(layer.feature_count || 5000, MAX_GEOJSON_FEATURES);
      const featureCollection = await getFeatures(layer.id, { limit: fetchLimit });
      cachedFeatures = featureCollection.features;
      useCluster = (layer.feature_count ?? 0) > POINT_CLUSTER_THRESHOLD;
      this.map.addSource(sourceId, {
        type: 'geojson',
        data: featureCollection,
        cluster: useCluster,
        clusterMaxZoom: 14,
        clusterRadius: 50,
      });
    } else {
      const useTiles =
        MARTIN_URL && layer.table_name && (layer.feature_count ?? 0) > VECTOR_TILE_THRESHOLD;
      if (useTiles) {
        this.map.addSource(sourceId, {
          type: 'vector',
          tiles: [`${MARTIN_URL}/${layer.table_name}/{z}/{x}/{y}`],
          minzoom: 0,
          maxzoom: 22,
        });
        sourceLayerName = layer.table_name;
      } else {
        const fetchLimit = Math.min(layer.feature_count || 5000, MAX_GEOJSON_FEATURES);
        const featureCollection = await getFeatures(layer.id, { limit: fetchLimit });
        cachedFeatures = featureCollection.features;
        this.map.addSource(sourceId, { type: 'geojson', data: featureCollection });
      }
    }

    const sourceLayer = sourceLayerName ? { 'source-layer': sourceLayerName } : {};

    if (isPolygon) {
      const fillId = `ul-${layer.id}-fill`;
      this.map.addLayer({
        id: fillId, type: 'fill', source: sourceId, ...sourceLayer,
        paint: { 'fill-color': color, 'fill-opacity': 0.25 }
      });
      const lineId = `ul-${layer.id}-line`;
      this.map.addLayer({
        id: lineId, type: 'line', source: sourceId, ...sourceLayer,
        paint: { 'line-color': color, 'line-width': 1.5 }
      });
      layerIds.push(fillId, lineId);
    } else if (isLine) {
      const lineId = `ul-${layer.id}-line`;
      this.map.addLayer({
        id: lineId, type: 'line', source: sourceId, ...sourceLayer,
        paint: { 'line-color': color, 'line-width': 2 }
      });
      layerIds.push(lineId);
    } else if (isPoint) {
      // Three sub-layers when clustering: cluster bubble, count label,
      // unclustered points. Without clustering: just unclustered points
      // (the filter is benign — every feature is "unclustered").
      const pointId   = `ul-${layer.id}-circle`;
      const clusterId = `ul-${layer.id}-cluster`;
      const countId   = `ul-${layer.id}-cluster-count`;

      if (useCluster) {
        this.map.addLayer({
          id: clusterId, type: 'circle', source: sourceId,
          filter: ['has', 'point_count'],
          paint: {
            'circle-color': [
              'step', ['get', 'point_count'],
              color,                     // < 100
              100,  '#98c262',           // ≥ 100
              500,  '#e0a93a',           // ≥ 500
              2000, '#d65a51',           // ≥ 2000
            ],
            'circle-radius': [
              'step', ['get', 'point_count'],
              14, 100, 18, 500, 22, 2000, 28,
            ],
            'circle-stroke-color': '#fff',
            'circle-stroke-width': 1.5,
            'circle-opacity': 0.85,
          },
        });
        this.map.addLayer({
          id: countId, type: 'symbol', source: sourceId,
          filter: ['has', 'point_count'],
          layout: {
            'text-field': ['get', 'point_count_abbreviated'],
            'text-size': 11,
            'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
          },
          paint: { 'text-color': '#fff' },
        });
        layerIds.push(clusterId, countId);
      }

      this.map.addLayer({
        id: pointId, type: 'circle', source: sourceId,
        filter: useCluster ? ['!', ['has', 'point_count']] : undefined,
        paint: {
          'circle-radius': 5,
          'circle-color': color,
          'circle-stroke-color': '#fff',
          'circle-stroke-width': 1.5,
          'circle-opacity': 0.9
        }
      });
      layerIds.push(pointId);

      if (useCluster) {
        // Click a cluster → expand it (MapLibre's standard pattern)
        this.map.on('click', clusterId, e => {
          const f = this.map.queryRenderedFeatures(e.point, { layers: [clusterId] })[0];
          if (!f) return;
          const clusterIdNum = f.properties.cluster_id;
          this.map.getSource(sourceId).getClusterExpansionZoom(clusterIdNum, (err, zoom) => {
            if (err) return;
            this.map.easeTo({ center: f.geometry.coordinates, zoom: Math.min(zoom + 0.5, 16) });
          });
        });
        this.map.on('mouseenter', clusterId, () => { this.map.getCanvas().style.cursor = 'pointer'; });
        this.map.on('mouseleave', clusterId, () => { if (!APP.activeTool) this.map.getCanvas().style.cursor = ''; });
      }
    }

    // Wire interactivity (click popup + hover cursor) on individual feature
    // layers — clusters are handled separately above.
    const interactiveIds = layerIds.filter(id => id.endsWith('-fill') || id.endsWith('-circle') || id.endsWith('-line'));
    for (const lid of interactiveIds) {
      this.map.on('click', lid, e => this._showFeaturePopup(e, layer));
      this.map.on('mouseenter', lid, () => { this.map.getCanvas().style.cursor = 'pointer'; });
      this.map.on('mouseleave', lid, () => { if (!APP.activeTool) this.map.getCanvas().style.cursor = ''; });
    }

    this.loaded.set(layer.id, {
      sourceId, layerIds, name: layer.name,
      geometryType: layer.geometry_type, clustered: useCluster,
      features: cachedFeatures,   // null for vector-tile layers
    });

    if (Array.isArray(layer.bbox) && layer.bbox.length === 4) {
      this.map.fitBounds(
        [[layer.bbox[0], layer.bbox[1]], [layer.bbox[2], layer.bbox[3]]],
        { padding: 60, maxZoom: 12, duration: 800 }
      );
    }

    // Re-apply any saved style for this layer
    this._maybeReapplyStyle(layer);
  }

  async _showRaster(layer) {
    if (!TITILER_URL) {
      throw new Error('Raster server is not configured (VITE_TITILER_URL missing)');
    }
    if (!layer.storage_key) {
      throw new Error('Raster has no storage_key — ingestion may have failed');
    }

    const sourceId  = `ul-src-${layer.id}`;
    const rasterId  = `ul-${layer.id}-raster`;
    const cogUrl    = `s3://${S3_BUCKET}/${layer.storage_key}`;

    // Single-band rasters need a colormap + rescale; otherwise the raw
    // numeric values render as near-black grayscale. We fetch /statistics,
    // and use percentile_2..percentile_98 as the rescale (robust to
    // outliers), then apply the `magma` colormap which is bright across
    // the full range — visible on both dark and light basemaps.
    // For 3+ band rasters we assume RGB and let TiTiler use its defaults.
    const bands = Number(layer.band_count) || 1;
    const tileParams = new URLSearchParams({ url: cogUrl });
    if (bands < 3) {
      try {
        const statsResp = await fetch(`${TITILER_URL}/cog/statistics?${new URLSearchParams({ url: cogUrl })}`);
        if (statsResp.ok) {
          const stats = await statsResp.json();
          const b1 = stats.b1 || stats[Object.keys(stats)[0]];
          if (b1) {
            const lo = Number.isFinite(b1.percentile_2) ? b1.percentile_2 : b1.min;
            const hi = Number.isFinite(b1.percentile_98) ? b1.percentile_98 : b1.max;
            if (Number.isFinite(lo) && Number.isFinite(hi) && hi > lo) {
              tileParams.set('rescale', `${lo},${hi}`);
              tileParams.set('colormap_name', 'magma');
            }
          }
        }
      } catch (err) {
        console.warn('Raster statistics fetch failed; rendering with default scale', err);
      }
    }

    const tilejsonUrl = `${TITILER_URL}/cog/tilejson.json?${tileParams}`;
    const resp = await fetch(tilejsonUrl);
    if (!resp.ok) {
      const body = await resp.text().catch(() => '');
      throw new Error(`TiTiler tilejson failed: HTTP ${resp.status} ${body.slice(0, 120)}`);
    }
    const tj = await resp.json();

    this.map.addSource(sourceId, {
      type: 'raster',
      tiles: tj.tiles,
      tileSize: 256,
      bounds: tj.bounds,
      // minzoom 0 so MapLibre always tries tiles — TiTiler renders from
      // native COG at any zoom. maxzoom uses the COG's reported value so
      // MapLibre overzooms past it instead of 404-ing for missing pyramids.
      minzoom: 0,
      maxzoom: tj.maxzoom ?? 22,
      attribution: tj.attribution
    });
    this.map.addLayer({
      id: rasterId, type: 'raster', source: sourceId,
      paint: { 'raster-opacity': 0.92 }
    });

    this.loaded.set(layer.id, {
      sourceId, layerIds: [rasterId], name: layer.name, geometryType: null
    });

    // Fly to the layer's extent. Use tj.bounds when present (more precise
    // than the layer's stored bbox if reprojection rounded it).
    const fitBox = (Array.isArray(tj.bounds) && tj.bounds.length === 4)
      ? tj.bounds
      : (Array.isArray(layer.bbox) && layer.bbox.length === 4 ? layer.bbox : null);
    if (fitBox) {
      this.map.fitBounds(
        [[fitBox[0], fitBox[1]], [fitBox[2], fitBox[3]]],
        { padding: 60, maxZoom: 12, duration: 800 }
      );
    }
  }

  /**
   * Apply graduated symbology: optional `colorExpr` (a `step` expression
   * over the chosen attribute) and/or `sizeExpr` (also a step expression,
   * applied to circle-radius for points or line-width for lines).
   *
   * Polygons honour only color (size doesn't apply); points/lines can
   * vary color, size, or both.
   *
   * Returns false if the layer isn't on the map yet.
   *
   * Backward-compatible alias: `applyChoropleth(id, expr)` still works.
   */
  applyGraduated(layerId, { colorExpr, sizeExpr } = {}) {
    const entry = this.loaded.get(layerId);
    if (!entry) return false;

    const hasFill = entry.layerIds.some(id => id.endsWith('-fill'));
    for (const lid of entry.layerIds) {
      if (lid.endsWith('-fill')) {
        if (colorExpr) {
          this.map.setPaintProperty(lid, 'fill-color', colorExpr);
          this.map.setPaintProperty(lid, 'fill-opacity', 0.65);
        }
      } else if (lid.endsWith('-circle')) {
        if (colorExpr) this.map.setPaintProperty(lid, 'circle-color',  colorExpr);
        if (sizeExpr)  this.map.setPaintProperty(lid, 'circle-radius', sizeExpr);
      } else if (lid.endsWith('-line') && !hasFill) {
        if (colorExpr) this.map.setPaintProperty(lid, 'line-color', colorExpr);
        if (sizeExpr)  this.map.setPaintProperty(lid, 'line-width', sizeExpr);
      }
    }
    entry.styled = 'graduated';
    return true;
  }

  /** Backward-compat: callers that pass a bare color expression. */
  applyChoropleth(layerId, expression) {
    return this.applyGraduated(layerId, { colorExpr: expression });
  }

  /**
   * Apply categorical color expression to whichever paint property fits
   * the layer's geometry. `matchExpr` is built by `matchExpression()`.
   */
  applyCategorical(layerId, matchExpr) {
    const entry = this.loaded.get(layerId);
    if (!entry) return false;

    const hasFill = entry.layerIds.some(id => id.endsWith('-fill'));
    for (const lid of entry.layerIds) {
      if (lid.endsWith('-fill')) {
        this.map.setPaintProperty(lid, 'fill-color', matchExpr);
        this.map.setPaintProperty(lid, 'fill-opacity', 0.65);
      } else if (lid.endsWith('-circle')) {
        this.map.setPaintProperty(lid, 'circle-color', matchExpr);
      } else if (lid.endsWith('-line') && !hasFill) {
        this.map.setPaintProperty(lid, 'line-color', matchExpr);
      }
    }
    entry.styled = 'categorical';
    return true;
  }

  /** Restore the layer's default solid-color paint. */
  clearChoropleth(layerId) {
    const entry = this.loaded.get(layerId);
    if (!entry) return false;

    const color = this.paletteFor(layerId);
    for (const lid of entry.layerIds) {
      if (lid.endsWith('-fill')) {
        this.map.setPaintProperty(lid, 'fill-color', color);
        this.map.setPaintProperty(lid, 'fill-opacity', 0.25);
      } else if (lid.endsWith('-circle')) {
        this.map.setPaintProperty(lid, 'circle-color', color);
      } else if (lid.endsWith('-line')) {
        this.map.setPaintProperty(lid, 'line-color', color);
      }
    }
    entry.choropleth = false;
    return true;
  }

  /**
   * Apply a uniform style across the whole layer (single color / size /
   * opacity). Each option is optional — only the ones provided are set.
   */
  applySingleStyle(layerId, opts = {}) {
    const entry = this.loaded.get(layerId);
    if (!entry) return false;

    const { color, opacity, radius, lineWidth, fillOpacity } = opts;

    for (const lid of entry.layerIds) {
      if (lid.endsWith('-fill')) {
        if (color !== undefined)       this.map.setPaintProperty(lid, 'fill-color', color);
        if (fillOpacity !== undefined) this.map.setPaintProperty(lid, 'fill-opacity', fillOpacity);
      } else if (lid.endsWith('-circle')) {
        if (color !== undefined)   this.map.setPaintProperty(lid, 'circle-color', color);
        if (opacity !== undefined) this.map.setPaintProperty(lid, 'circle-opacity', opacity);
        if (radius !== undefined)  this.map.setPaintProperty(lid, 'circle-radius', radius);
      } else if (lid.endsWith('-line')) {
        if (color !== undefined)     this.map.setPaintProperty(lid, 'line-color', color);
        if (opacity !== undefined)   this.map.setPaintProperty(lid, 'line-opacity', opacity);
        if (lineWidth !== undefined) this.map.setPaintProperty(lid, 'line-width', lineWidth);
      }
    }
    entry.choropleth = false;
    return true;
  }

  /** Reset to the default palette + opacities for this layer's geometry. */
  resetStyle(layerId) {
    const entry = this.loaded.get(layerId);
    if (!entry) return false;
    const color = this.paletteFor(layerId);
    return this.applySingleStyle(layerId, {
      color,
      opacity: 0.9,
      radius: 5,
      lineWidth: 1.5,
      fillOpacity: 0.25,
    });
  }

  /** If a saved style exists, rebuild and apply it. */
  _maybeReapplyStyle(layer) {
    const saved = getStyle(layer.id);
    if (!saved?.applied) return;

    if (saved.type === 'bivariate' && saved.breaksA && saved.breaksB) {
      const palette = BIVARIATE_PALETTES[saved.palette] || BIVARIATE_PALETTES['pink-cyan'];
      const colorExpr = bivariateExpression(
        saved.columnA, saved.breaksA,
        saved.columnB, saved.breaksB,
        palette,
      );
      this.applyGraduated(layer.id, { colorExpr });
      pushBivariateLegend(layer.name, saved.columnA, saved.columnB, palette);
    } else if (saved.type === 'graduated' || saved.breaks) {
      const colors = sampleRamp(saved.ramp, saved.bins);
      const colorExpr = saved.varies !== 'size'
        ? stepExpression(saved.column, saved.breaks, colors)
        : undefined;
      let sizeExpr;
      if ((saved.varies === 'size' || saved.varies === 'both')
          && typeof saved.sizeMin === 'number' && typeof saved.sizeMax === 'number') {
        sizeExpr = stepExpression(saved.column, saved.breaks,
                                  rampedSizes(saved.sizeMin, saved.sizeMax, saved.bins));
      }
      this.applyGraduated(layer.id, { colorExpr, sizeExpr });
      pushChoroplethLegend(layer.name, saved.column, saved, colors);
    } else if (saved.type === 'categorical') {
      const expr = matchExpression(saved.column, saved.mapping, saved.defaultColor);
      this.applyCategorical(layer.id, expr);
      pushCategoricalLegend(layer.name, saved.column, saved.mapping, saved.defaultColor);
    } else if (saved.type === 'single') {
      this.applySingleStyle(layer.id, saved);
    }
  }

  hide(id) { this._setVisibility(id, false); }

  remove(id) {
    const entry = this.loaded.get(id);
    if (!entry) return;
    for (const lid of entry.layerIds) {
      if (this.map.getLayer(lid)) this.map.removeLayer(lid);
    }
    if (this.map.getSource(entry.sourceId)) this.map.removeSource(entry.sourceId);
    this.loaded.delete(id);
  }

  _setVisibility(id, visible) {
    const entry = this.loaded.get(id);
    if (!entry) return;
    for (const lid of entry.layerIds) {
      this.map.setLayoutProperty(lid, 'visibility', visible ? 'visible' : 'none');
    }
  }

  _showFeaturePopup(e, layer) {
    // If a spatial tool is active, hand the click to it instead of opening
    // the popup. Dynamic import dodges the circular dependency between
    // userLayers ↔ analysis modules.
    if (APP.activeTool === 'isochrone') {
      import('../analysis/isochrone.js').then(m => m.runIsochrone(this.map, e.lngLat));
      return;
    }
    if (APP.activeTool === 'buffer') {
      import('../analysis/buffer.js').then(m => m.runBuffer(this.map, e.lngLat));
      return;
    }

    const f = e.features?.[0];
    if (!f) return;
    const props = f.properties || {};

    // Pick a reasonable title field if available
    const titleKey = ['name', 'Name', 'NAME', 'title', 'label', 'id'].find(k => k in props);
    const title = titleKey ? props[titleKey] : layer.name;

    const rows = Object.entries(props)
      .filter(([k]) => k !== titleKey)
      .map(([k, v]) => {
        const val = v === null || v === undefined || v === '' ? '–' : String(v);
        return `
          <div style="font-size:12px;display:flex;justify-content:space-between;gap:10px;padding:4px 0;border-top:1px solid rgba(255,255,255,.06)">
            <span style="color:#94a3bf">${escape(k)}</span>
            <span style="font-weight:600;font-family:var(--mono);font-size:11.5px;text-align:right;max-width:170px;overflow-wrap:anywhere">${escape(val)}</span>
          </div>`;
      })
      .join('');

    const html = `
      <div style="font-size:14px;font-weight:700;margin-bottom:2px">${escape(title)}</div>
      <div style="font-size:11px;color:#94a3bf;margin-bottom:6px">${escape(layer.name)} · ${escape(f.geometry?.type || '')}</div>
      ${rows || '<div style="font-size:11.5px;color:#94a3bf">No attributes</div>'}
    `;

    new maplibregl.Popup({ maxWidth: '320px' })
      .setLngLat(e.lngLat)
      .setHTML(html)
      .addTo(this.map);
  }
}

function escape(s) {
  return String(s ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
}
